import { useState, useEffect, useCallback } from 'react'
import React from 'react'
import { Helmet } from 'react-helmet-async'
import { Grid } from '@mui/material'
import {
  TableCellBox,
  CustomizedButton,
  HeaderTypography,
  OuterSurface,
  StyledDiv,
  SubHeadingTypography,
  MessageBox,
  IconWrapper,
  MessageDiv,
  CustomTextField,
} from './style'
import { useTranslation } from 'react-i18next'
import { InformationIcon } from '../../components/icons/myIcons'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import biomassImg from '../EnergyMix/Assets/biomasse_oli.svg'
import gridImg from '../EnergyMix/Assets/netzstrom_oli.svg'
import solarImg from '../EnergyMix/Assets/solar_oli.svg'
import wasserImg from '../EnergyMix/Assets/wasserkraft_oli.svg'
import windImg from '../EnergyMix/Assets/wind_oli.svg'
import DiscountOverview from './DiscountOverview'
import { bffService } from '../../services/utils/axios'
import { formatDecimal } from '../../services/utils/format-numbers'
import { NumericFormat } from 'react-number-format'
import { getOperatorId } from '../../services/utils/helpers'

const NumericFormatCustomTextField = React.forwardRef(
  function NumericFormatCustomTextField(props, ref) {
    const { allowNegativeValue, ...rest } = props
    return <CustomTextField {...rest} ref={ref} />
  }
)

const ENERGY_PLANT_TYPE_TO_UI_FIELD = {
  BIOMASS: 'bioMasse',
  SOLAR: 'solar',
  WIND: 'windStrom',
  HYDRO: 'wasserKraft',
  GRID: 'restStrom',
}

/**
 * GET /operator-energy-costs returns per plant type either a legacy number or:
 * { "price": number, "lastUpdated": ISO-8601 string }
 */
const parseCostNumber = (value) => {
  if (value == null) {
    return null
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value
  }
  if (typeof value === 'string' && value.trim() !== '') {
    const n = Number(value.replace(',', '.'))
    return Number.isFinite(n) ? n : null
  }
  return null
}

const extractOperatorEnergyCostsPayload = (raw) => {
  if (!raw || typeof raw !== 'object') {
    return {}
  }
  const hasRoleShape = (obj) =>
    obj &&
    typeof obj === 'object' &&
    ('PRODUCER' in obj || 'CONSUMER' in obj)

  if (hasRoleShape(raw)) {
    return raw
  }
  if (
    raw.data &&
    typeof raw.data === 'object' &&
    hasRoleShape(raw.data)
  ) {
    return raw.data
  }
  return raw
}

/** Normalizes GET /effective-operator-energy-costs-by-year (per-role month arrays). */
const extractEffectiveByYearPayload = (raw) => {
  const pick = (obj) => ({
    PRODUCER: Array.isArray(obj?.PRODUCER) ? obj.PRODUCER : [],
    CONSUMER: Array.isArray(obj?.CONSUMER) ? obj.CONSUMER : [],
  })
  if (!raw || typeof raw !== 'object') {
    return pick({})
  }
  if (Array.isArray(raw.PRODUCER) || Array.isArray(raw.CONSUMER)) {
    return pick(raw)
  }
  if (raw.data && typeof raw.data === 'object') {
    return pick(raw.data)
  }
  return pick({})
}

/** Maps per-role snapshot from GET /operator-energy-costs to table row fields. */
const buildCurrentCostsFromSnapshot = (roleCosts) => {
  if (
    !roleCosts ||
    typeof roleCosts !== 'object' ||
    Array.isArray(roleCosts)
  ) {
    return undefined
  }
  const out = {}
  for (const [energyType, entry] of Object.entries(roleCosts)) {
    const field =
      ENERGY_PLANT_TYPE_TO_UI_FIELD[String(energyType).toUpperCase()]
    if (field == null) {
      continue
    }

    let priceRaw = entry
    let lastUpdated = null
    if (entry && typeof entry === 'object' && !Array.isArray(entry)) {
      priceRaw = entry.price
      if (entry.lastUpdated != null && String(entry.lastUpdated).trim() !== '') {
        lastUpdated = entry.lastUpdated
      }
    }

    const n = parseCostNumber(priceRaw)
    if (n != null) {
      out[field] = n
      if (lastUpdated != null) {
        out[`${field}UpdateTimeStamp`] = lastUpdated
      }
    }
  }
  return Object.keys(out).length ? out : undefined
}

const isLastDayOfMonth = (date) => {
  const endOfMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate()
  return date.getDate() === endOfMonth
}

const getMonthDiffFromCurrent = (targetDate, currentDate) =>
  (targetDate.getFullYear() - currentDate.getFullYear()) * 12 +
  (targetDate.getMonth() - currentDate.getMonth())

const shouldShowEffectiveValues = (targetDate, currentDate) => {
  const monthDiff = getMonthDiffFromCurrent(targetDate, currentDate)

  if (monthDiff < 2) {
    return true
  }

  if (monthDiff === 2) {
    return isLastDayOfMonth(currentDate)
  }

  return false
}

const transformEffectiveChartData = (data) => {
  const transformedData = []
  const monthlyData = Array.isArray(data) ? data : data?.months ?? []
  const today = new Date()

  for (const item of monthlyData) {
    const date = new Date(item.date)
    date.setTime(date.getTime() + date.getTimezoneOffset() * 60000)
    const showValues = shouldShowEffectiveValues(date, today)
    const t = {
      time: date.toLocaleString('default', { month: 'long' }),
      bioMasse: null,
      solar: null,
      windStrom: null,
      wasserKraft: null,
      restStrom: null,
    }
    const effectiveCosts = item?.effectiveEnergyCosts ?? {}

    if (showValues) {
      t.bioMasse = effectiveCosts.BIOMASS ?? 0.0
      t.solar = effectiveCosts.SOLAR ?? 0.0
      t.windStrom = effectiveCosts.WIND ?? 0.0
      t.wasserKraft = effectiveCosts.HYDRO ?? 0.0
      t.restStrom = effectiveCosts.GRID ?? 0.0
    }

    transformedData.push(t)
  }

  return transformedData
}

/**
 * This is the main entry component for Aschlage screen on dashboard.
 * @returns {React.ReactNode} - It holds all the data related price adjustments, graph and table.
 * Also it holds the form to set the prices.
 *
 */
const Abschlag = () => {
  const { t } = useTranslation()
  const [biomassPrice, setBiomassPrice] = useState('')
  const [solarPrice, setSolarPrice] = useState('')
  const [windEnergyPrice, setWindEnergyPrice] = useState('')
  const [wasserEnergyPrice, setWasserEnergyPrice] = useState('')
  const [consumerBiomassPrice, setConsumerBiomassPrice] = useState('')
  const [consumerSolarPrice, setConsumerSolarPrice] = useState('')
  const [consumerWindEnergyPrice, setConsumerWindEnergyPrice] = useState('')
  const [consumerWasserEnergyPrice, setConsumerWasserEnergyPrice] = useState('')
  const [consumerGridEnergyPrice, setConsumerGridEnergyPrice] = useState('')
  const [overviewError, setOverviewError] = useState(null)
  const [data, setData] = useState({
    PRODUCER: { currentCosts: undefined, chartData: [] },
    CONSUMER: { currentCosts: undefined, chartData: [] },
  })
  const [energyRole, setEnergyRole] = useState('PRODUCER')

  const fetchData = useCallback(async () => {
    const year = new Date().getFullYear()
    const [operatorResponse, effectiveByYearResponse] = await Promise.all([
      bffService.get('/operator-energy-costs'),
      bffService.get('/effective-operator-energy-costs-by-year', {
        params: { year },
      }),
    ])
    const body = extractOperatorEnergyCostsPayload(operatorResponse.data)
    const effectiveByYear = extractEffectiveByYearPayload(
      effectiveByYearResponse.data
    )
    setData({
      PRODUCER: {
        currentCosts: buildCurrentCostsFromSnapshot(body.PRODUCER),
        chartData: transformEffectiveChartData(effectiveByYear.PRODUCER),
      },
      CONSUMER: {
        currentCosts: buildCurrentCostsFromSnapshot(body.CONSUMER),
        chartData: transformEffectiveChartData(effectiveByYear.CONSUMER),
      },
    })
  }, [])
  useEffect(() => {
    fetchData()
  }, [fetchData])

  /**
   * This is API call to post the values of prices of energy types.
   *
   * @param {string} key - It could be all the available energy type , i.e. bioMasse, solar, wasserKraft, windStrom and restStrom (restStrom is only for Consumer)
   * @param {number} value - the value of price of energy
   * @param {string} userType - It can be either PRODUCER and CONSUMER
   */
  const handleUpdate = (key, value, userType) => {
    if (!value || value.toString().trim() === '') {
      return
    }
    const operatorId = getOperatorId()
    if (!operatorId) {
      console.error('Operator ID not found')
      return
    }
    const requestBody = {
      companyId: operatorId,
      energyPlantType: key,
      energyRole: userType,
      price: parseFloat(value.replace(',', '.')),
    }
    bffService
      .post('/operator-energy-costs', requestBody)
      .then(() => {
        fetchData()
      })
      .catch((err) => {
        console.error('Update failed', err)
      })
  }

  // Function to get local date and time from Timestamp of data.
  const formatTimeStamp = (timeStamp) => {
    const utcDate = new Date(timeStamp)

    // Get the local time zone offset in minutes
    const timeZoneOffset = utcDate.getTimezoneOffset() * 60000
    const localDate = new Date(utcDate.getTime() - timeZoneOffset)

    const formattedDate = localDate.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    })

    const formattedTime = localDate.toLocaleTimeString('de-DE', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    })

    return { date: formattedDate, time: formattedTime }
  }
  return (
    <>
      <Helmet title="Abschlag" />

      <Grid
        justifyContent="space-between"
        container
        spacing={10}
        sx={{ visibility: 'transparent' }}
      >
        <Grid item mb={14} ml={4}>
          <HeaderTypography variant="h2" gutterBottom display="inline">
            {t('LBLAbschlag')}
          </HeaderTypography>
        </Grid>
      </Grid>
      <OuterSurface>
        <StyledDiv>
          <SubHeadingTypography variant="h1">
            {t('LBLAbschlagAnpassen')}
          </SubHeadingTypography>
          <MessageBox>
            <IconWrapper>
              <InformationIcon style={{ fontSize: 44 }} />
            </IconWrapper>
            <MessageDiv>
              <SubHeadingTypography variant="h6">
                {t('LBLAbschlagMessage')}
              </SubHeadingTypography>
            </MessageDiv>
          </MessageBox>
          <SubHeadingTypography variant="h1">
            {t('LBLAbschlägeErzeugung')}
          </SubHeadingTypography>

          {/* table to hold the energy types for production price reduction */}
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow
                  sx={{
                    borderBottom: 2.5,
                    borderColor: (theme) => theme.tokens.secondary,
                  }}
                >
                  <TableCell>
                    <SubHeadingTypography variant="h5">
                      {t('LBLEnergieTyp')}
                    </SubHeadingTypography>
                  </TableCell>
                  <TableCell>
                    <SubHeadingTypography variant="h5">
                      {t('LBLZuletztAktualisiert')}
                    </SubHeadingTypography>
                  </TableCell>
                  <TableCell>
                    <SubHeadingTypography variant="h5">
                      {t('LBLAktuellerAbschlag')}
                    </SubHeadingTypography>
                  </TableCell>
                  <TableCell>
                    <SubHeadingTypography variant="h5">
                      {t('LBLNeuerAbschlag')}
                    </SubHeadingTypography>
                  </TableCell>
                  <TableCell>
                    <SubHeadingTypography variant="h5"></SubHeadingTypography>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <>
                  <TableRow
                    style={{
                      borderTop: '1px solid',
                      color: (theme) => theme.tokens.secondary,
                    }}
                  >
                    <TableCell>
                      <TableCellBox>
                        <img src={biomassImg} alt="Biomass" />
                        <SubHeadingTypography variant="h6">
                          Biomasse
                        </SubHeadingTypography>
                      </TableCellBox>
                    </TableCell>
                    <TableCell>
                      {data?.['PRODUCER']?.currentCosts
                        ?.bioMasseUpdateTimeStamp != null
                        ? formatTimeStamp(
                            data?.['PRODUCER']?.currentCosts
                              ?.bioMasseUpdateTimeStamp
                          ).date +
                          ' ' +
                          formatTimeStamp(
                            data?.['PRODUCER']?.currentCosts
                              ?.bioMasseUpdateTimeStamp
                          ).time +
                          ' Uhr'
                        : '--'}
                    </TableCell>
                    <TableCell>
                      {data?.['PRODUCER']?.currentCosts?.bioMasse != null
                        ? formatDecimal(
                            data?.['PRODUCER']?.currentCosts?.bioMasse
                          )
                        : '--'}
                    </TableCell>
                    <TableCell>
                      <NumericFormat
                        margin="none"
                        id="bioMasse"
                        name="bioMasse"
                        label={t('LBLProfitTextField')}
                        value={biomassPrice}
                        customInput={NumericFormatCustomTextField}
                        onValueChange={(value) =>
                          setBiomassPrice(value.formattedValue)
                        }
                        decimalSeparator=","
                        allowNegativeValue
                        InputProps={{
                          title: 'Bitte füllen Sie dieses Feld aus',
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <CustomizedButton
                        variant="contained"
                        disabled={!biomassPrice}
                        onClick={() => {
                          handleUpdate('BIOMASS', biomassPrice, 'PRODUCER')
                          setBiomassPrice('')
                        }}
                      >
                        {t('LBLUpdate')}
                      </CustomizedButton>
                    </TableCell>
                  </TableRow>

                  <TableRow
                    style={{
                      borderTop: '1px solid',
                      color: (theme) => theme.tokens.secondary,
                    }}
                  >
                    <TableCell>
                      <TableCellBox>
                        <img src={solarImg} alt="SolarEnergy" />
                        <SubHeadingTypography variant="h6">
                          Solar
                        </SubHeadingTypography>
                      </TableCellBox>
                    </TableCell>
                    <TableCell>
                      {data?.['PRODUCER']?.currentCosts?.solarUpdateTimeStamp !=
                      null
                        ? formatTimeStamp(
                            data?.['PRODUCER']?.currentCosts
                              ?.solarUpdateTimeStamp
                          ).date +
                          ' ' +
                          formatTimeStamp(
                            data?.['PRODUCER']?.currentCosts
                              ?.solarUpdateTimeStamp
                          ).time +
                          ' Uhr'
                        : '--'}
                    </TableCell>
                    <TableCell>
                      {data?.['PRODUCER']?.currentCosts?.solar != null
                        ? formatDecimal(data?.['PRODUCER']?.currentCosts?.solar)
                        : '--'}
                    </TableCell>
                    <TableCell>
                      <NumericFormat
                        fullWidth
                        margin="none"
                        id="solar"
                        name="solar"
                        label={t('LBLProfitTextField')}
                        value={solarPrice}
                        customInput={NumericFormatCustomTextField}
                        onValueChange={(value) =>
                          setSolarPrice(value.formattedValue)
                        }
                        decimalSeparator=","
                        allowNegativeValue
                        InputProps={{
                          title: 'Bitte füllen Sie dieses Feld aus',
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <CustomizedButton
                        variant="contained"
                        disabled={!solarPrice}
                        onClick={() => {
                          handleUpdate('SOLAR', solarPrice, 'PRODUCER')
                          setSolarPrice('')
                        }}
                      >
                        {t('LBLUpdate')}
                      </CustomizedButton>
                    </TableCell>
                  </TableRow>

                  <TableRow
                    style={{
                      borderTop: '1px solid',
                      color: (theme) => theme.tokens.secondary,
                    }}
                  >
                    <TableCell>
                      <TableCellBox>
                        <img src={wasserImg} alt="HydroPower" />
                        <SubHeadingTypography variant="h6">
                          Wasserkraft
                        </SubHeadingTypography>
                      </TableCellBox>
                    </TableCell>
                    <TableCell>
                      {data?.['PRODUCER']?.currentCosts
                        ?.wasserKraftUpdateTimeStamp != null
                        ? formatTimeStamp(
                            data?.['PRODUCER']?.currentCosts
                              ?.wasserKraftUpdateTimeStamp
                          ).date +
                          ' ' +
                          formatTimeStamp(
                            data?.['PRODUCER']?.currentCosts
                              ?.wasserKraftUpdateTimeStamp
                          ).time +
                          ' Uhr'
                        : '--'}
                    </TableCell>
                    <TableCell>
                      {data?.['PRODUCER']?.currentCosts?.wasserKraft != null
                        ? formatDecimal(
                            data?.['PRODUCER']?.currentCosts?.wasserKraft
                          )
                        : '--'}
                    </TableCell>
                    <TableCell>
                      <NumericFormat
                        fullWidth
                        margin="none"
                        id="wasserKraft"
                        name="wasserKraft"
                        label={t('LBLProfitTextField')}
                        value={wasserEnergyPrice}
                        customInput={NumericFormatCustomTextField}
                        onValueChange={(value) =>
                          setWasserEnergyPrice(value.formattedValue)
                        }
                        decimalSeparator=","
                        allowNegativeValue
                        InputProps={{
                          title: 'Bitte füllen Sie dieses Feld aus',
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <CustomizedButton
                        variant="contained"
                        disabled={!wasserEnergyPrice}
                        onClick={() => {
                          handleUpdate('HYDRO', wasserEnergyPrice, 'PRODUCER')
                          setWasserEnergyPrice('')
                        }}
                      >
                        {t('LBLUpdate')}
                      </CustomizedButton>
                    </TableCell>
                  </TableRow>

                  <TableRow
                    style={{
                      borderTop: '1px solid',
                      color: (theme) => theme.tokens.secondary,
                    }}
                  >
                    <TableCell>
                      <TableCellBox>
                        <img src={windImg} alt="WindEnergy" />
                        <SubHeadingTypography variant="h6">
                          Windstrom
                        </SubHeadingTypography>
                      </TableCellBox>
                    </TableCell>
                    <TableCell>
                      {data?.['PRODUCER']?.currentCosts
                        ?.windStromUpdateTimeStamp != null
                        ? formatTimeStamp(
                            data?.['PRODUCER']?.currentCosts
                              ?.windStromUpdateTimeStamp
                          ).date +
                          ' ' +
                          formatTimeStamp(
                            data?.['PRODUCER']?.currentCosts
                              ?.windStromUpdateTimeStamp
                          ).time +
                          ' Uhr'
                        : '--'}
                    </TableCell>
                    <TableCell>
                      {data?.['PRODUCER']?.currentCosts?.windStrom != null
                        ? formatDecimal(
                            data?.['PRODUCER']?.currentCosts?.windStrom
                          )
                        : '--'}
                    </TableCell>
                    <TableCell>
                      <NumericFormat
                        fullWidth
                        margin="none"
                        id="windStrom"
                        name="windStrom"
                        label={t('LBLProfitTextField')}
                        value={windEnergyPrice}
                        customInput={NumericFormatCustomTextField}
                        onValueChange={(value) =>
                          setWindEnergyPrice(value.formattedValue)
                        }
                        decimalSeparator=","
                        allowNegativeValue
                        InputProps={{
                          title: 'Bitte füllen Sie dieses Feld aus',
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <CustomizedButton
                        variant="contained"
                        disabled={!windEnergyPrice}
                        onClick={() => {
                          handleUpdate('WIND', windEnergyPrice, 'PRODUCER')
                          setWindEnergyPrice('')
                        }}
                      >
                        {t('LBLUpdate')}
                      </CustomizedButton>
                    </TableCell>
                  </TableRow>
                </>
              </TableBody>
            </Table>
          </TableContainer>

          <SubHeadingTypography variant="h1">
            {t('LBLCunsumptionPrice')}
          </SubHeadingTypography>

          {/* Table to hold the energy types for Consumption price reduction */}
          <TableContainer>
            <Table size="medium">
              <TableHead>
                <TableRow
                  sx={{
                    borderBottom: 2.5,
                    borderColor: (theme) => theme.tokens.secondary,
                  }}
                >
                  <TableCell>
                    <SubHeadingTypography variant="h5">
                      {t('LBLEnergieTyp')}
                    </SubHeadingTypography>
                  </TableCell>
                  <TableCell>
                    <SubHeadingTypography variant="h5">
                      {t('LBLZuletztAktualisiert')}
                    </SubHeadingTypography>
                  </TableCell>
                  <TableCell>
                    <SubHeadingTypography variant="h5">
                      {t('LBLAktuellerAbschlag')}
                    </SubHeadingTypography>
                  </TableCell>
                  <TableCell>
                    <SubHeadingTypography variant="h5">
                      {t('LBLNeuerAbschlag')}
                    </SubHeadingTypography>
                  </TableCell>
                  <TableCell>
                    <SubHeadingTypography variant="h5"></SubHeadingTypography>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <>
                  <TableRow
                    style={{
                      borderTop: '1px solid',
                      color: (theme) => theme.tokens.secondary,
                    }}
                  >
                    <TableCell>
                      <TableCellBox>
                        <img src={biomassImg} alt="Biomass" />
                        <SubHeadingTypography variant="h6">
                          Biomasse
                        </SubHeadingTypography>
                      </TableCellBox>
                    </TableCell>
                    <TableCell>
                      {data['CONSUMER']?.currentCosts
                        ?.bioMasseUpdateTimeStamp != null
                        ? formatTimeStamp(
                            data?.['CONSUMER']?.currentCosts
                              ?.bioMasseUpdateTimeStamp
                          ).date +
                          ' ' +
                          formatTimeStamp(
                            data?.['CONSUMER']?.currentCosts
                              ?.bioMasseUpdateTimeStamp
                          ).time +
                          ' Uhr'
                        : '--'}
                    </TableCell>
                    <TableCell>
                      {data['CONSUMER']?.currentCosts?.bioMasse != null
                        ? formatDecimal(
                            data['CONSUMER']?.currentCosts?.bioMasse
                          )
                        : '--'}
                    </TableCell>
                    <TableCell>
                      <NumericFormat
                        margin="none"
                        id="consumerBiomassPrice"
                        name="consumerBiomassPrice"
                        label={t('LBLProfitTextField')}
                        value={consumerBiomassPrice}
                        customInput={NumericFormatCustomTextField}
                        onValueChange={(value) =>
                          setConsumerBiomassPrice(value.formattedValue)
                        }
                        decimalSeparator=","
                        allowNegativeValue
                        InputProps={{
                          title: 'Bitte füllen Sie dieses Feld aus',
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <CustomizedButton
                        variant="contained"
                        disabled={!consumerBiomassPrice}
                        onClick={() => {
                          handleUpdate(
                            'BIOMASS',
                            consumerBiomassPrice,
                            'CONSUMER'
                          )
                          setConsumerBiomassPrice('')
                        }}
                      >
                        {t('LBLUpdate')}
                      </CustomizedButton>
                    </TableCell>
                  </TableRow>

                  <TableRow
                    style={{
                      borderTop: '1px solid',
                      color: (theme) => theme.tokens.secondary,
                    }}
                  >
                    <TableCell>
                      <TableCellBox>
                        <img src={solarImg} alt="SolarEnergy" />
                        <SubHeadingTypography variant="h6">
                          Solar
                        </SubHeadingTypography>
                      </TableCellBox>
                    </TableCell>
                    <TableCell>
                      {data['CONSUMER']?.currentCosts?.solarUpdateTimeStamp !=
                      null
                        ? formatTimeStamp(
                            data['CONSUMER']?.currentCosts?.solarUpdateTimeStamp
                          ).date +
                          ' ' +
                          formatTimeStamp(
                            data['CONSUMER']?.currentCosts?.solarUpdateTimeStamp
                          ).time +
                          ' Uhr'
                        : '--'}
                    </TableCell>
                    <TableCell>
                      {data['CONSUMER']?.currentCosts?.solar != null
                        ? formatDecimal(data['CONSUMER']?.currentCosts?.solar)
                        : '--'}
                    </TableCell>
                    <TableCell>
                      <NumericFormat
                        fullWidth
                        margin="none"
                        id="consumerSolarPrice"
                        name="consumerSolarPrice"
                        label={t('LBLProfitTextField')}
                        value={consumerSolarPrice}
                        customInput={NumericFormatCustomTextField}
                        onValueChange={(value) =>
                          setConsumerSolarPrice(value.formattedValue)
                        }
                        decimalSeparator=","
                        allowNegativeValue
                        InputProps={{
                          title: 'Bitte füllen Sie dieses Feld aus',
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <CustomizedButton
                        variant="contained"
                        disabled={!consumerSolarPrice}
                        onClick={() => {
                          handleUpdate('SOLAR', consumerSolarPrice, 'CONSUMER')
                          setConsumerSolarPrice('')
                        }}
                      >
                        {t('LBLUpdate')}
                      </CustomizedButton>
                    </TableCell>
                  </TableRow>

                  <TableRow
                    style={{
                      borderTop: '1px solid',
                      color: (theme) => theme.tokens.secondary,
                    }}
                  >
                    <TableCell>
                      <TableCellBox>
                        <img src={wasserImg} alt="HydroPower" />
                        <SubHeadingTypography variant="h6">
                          Wasserkraft
                        </SubHeadingTypography>
                      </TableCellBox>
                    </TableCell>
                    <TableCell>
                      {data?.['CONSUMER']?.currentCosts
                        ?.wasserKraftUpdateTimeStamp != null
                        ? formatTimeStamp(
                            data?.['CONSUMER']?.currentCosts
                              ?.wasserKraftUpdateTimeStamp
                          ).date +
                          ' ' +
                          formatTimeStamp(
                            data?.['CONSUMER']?.currentCosts
                              ?.wasserKraftUpdateTimeStamp
                          ).time +
                          ' Uhr'
                        : '--'}
                    </TableCell>
                    <TableCell>
                      {data['CONSUMER']?.currentCosts?.wasserKraft != null
                        ? formatDecimal(
                            data['CONSUMER']?.currentCosts?.wasserKraft
                          )
                        : '--'}
                    </TableCell>
                    <TableCell>
                      <NumericFormat
                        fullWidth
                        margin="none"
                        id="consumerWasserEnergyPrice"
                        name="consumerWasserEnergyPrice"
                        label={t('LBLProfitTextField')}
                        value={consumerWasserEnergyPrice}
                        customInput={NumericFormatCustomTextField}
                        onValueChange={(value) =>
                          setConsumerWasserEnergyPrice(value.formattedValue)
                        }
                        decimalSeparator=","
                        allowNegativeValue
                        InputProps={{
                          title: 'Bitte füllen Sie dieses Feld aus',
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <CustomizedButton
                        variant="contained"
                        disabled={!consumerWasserEnergyPrice}
                        onClick={() => {
                          handleUpdate(
                            'HYDRO',
                            consumerWasserEnergyPrice,
                            'CONSUMER'
                          )
                          setConsumerWasserEnergyPrice('')
                        }}
                      >
                        {t('LBLUpdate')}
                      </CustomizedButton>
                    </TableCell>
                  </TableRow>

                  <TableRow
                    style={{
                      borderTop: '1px solid',
                      color: (theme) => theme.tokens.secondary,
                    }}
                  >
                    <TableCell>
                      <TableCellBox>
                        <img src={windImg} alt="WindEnergy" />
                        <SubHeadingTypography variant="h6">
                          Windstrom
                        </SubHeadingTypography>
                      </TableCellBox>
                    </TableCell>
                    <TableCell>
                      {data?.['CONSUMER']?.currentCosts
                        ?.windStromUpdateTimeStamp != null
                        ? formatTimeStamp(
                            data?.['CONSUMER']?.currentCosts
                              ?.windStromUpdateTimeStamp
                          ).date +
                          ' ' +
                          formatTimeStamp(
                            data?.['CONSUMER']?.currentCosts
                              ?.windStromUpdateTimeStamp
                          ).time +
                          ' Uhr'
                        : '--'}
                    </TableCell>
                    <TableCell>
                      {data['CONSUMER']?.currentCosts?.windStrom != null
                        ? formatDecimal(
                            data['CONSUMER']?.currentCosts?.windStrom
                          )
                        : '--'}
                    </TableCell>
                    <TableCell>
                      <NumericFormat
                        fullWidth
                        margin="none"
                        id="consumerWindEnergyPrice"
                        name="consumerWindEnergyPrice"
                        value={consumerWindEnergyPrice}
                        label={t('LBLProfitTextField')}
                        customInput={NumericFormatCustomTextField}
                        onValueChange={(value) =>
                          setConsumerWindEnergyPrice(value.formattedValue)
                        }
                        decimalSeparator=","
                        allowNegativeValue
                        InputProps={{
                          title: 'Bitte füllen Sie dieses Feld aus',
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <CustomizedButton
                        variant="contained"
                        disabled={!consumerWindEnergyPrice}
                        onClick={() => {
                          handleUpdate(
                            'WIND',
                            consumerWindEnergyPrice,
                            'CONSUMER'
                          )
                          setConsumerWindEnergyPrice('')
                        }}
                      >
                        {t('LBLUpdate')}
                      </CustomizedButton>
                    </TableCell>
                  </TableRow>
                  <TableRow
                    sx={{
                      borderBottom: 'none',
                      borderTop: '1px solid',
                      color: (theme) => theme.tokens.secondary,
                    }}
                  >
                    <TableCell>
                      <TableCellBox>
                        <img src={gridImg} alt="GridEnergy" />
                        <SubHeadingTypography variant="h6">
                          Netzstrom
                        </SubHeadingTypography>
                      </TableCellBox>
                    </TableCell>
                    <TableCell>
                      {data?.['CONSUMER']?.currentCosts
                        ?.restStromUpdateTimeStamp != null
                        ? formatTimeStamp(
                            data?.['CONSUMER']?.currentCosts
                              ?.restStromUpdateTimeStamp
                          ).date +
                          ' ' +
                          formatTimeStamp(
                            data?.['CONSUMER']?.currentCosts
                              ?.restStromUpdateTimeStamp
                          ).time +
                          ' Uhr'
                        : '--'}
                    </TableCell>
                    <TableCell>
                      {data['CONSUMER']?.currentCosts?.restStrom != null
                        ? formatDecimal(
                            data['CONSUMER']?.currentCosts?.restStrom
                          )
                        : '--'}
                    </TableCell>
                    <TableCell>
                      <NumericFormat
                        fullWidth
                        margin="none"
                        id="consumerGridEnergyPrice"
                        name="consumerGridEnergyPrice"
                        value={consumerGridEnergyPrice}
                        label={t('LBLProfitTextField')}
                        customInput={NumericFormatCustomTextField}
                        onValueChange={(value) =>
                          setConsumerGridEnergyPrice(value.formattedValue)
                        }
                        decimalSeparator=","
                        allowNegativeValue
                        InputProps={{
                          title: 'Bitte füllen Sie dieses Feld aus',
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <CustomizedButton
                        variant="contained"
                        disabled={!consumerGridEnergyPrice}
                        onClick={() => {
                          handleUpdate(
                            'GRID',
                            consumerGridEnergyPrice,
                            'CONSUMER'
                          )
                          setConsumerGridEnergyPrice('')
                        }}
                      >
                        {t('LBLUpdate')}
                      </CustomizedButton>
                    </TableCell>
                  </TableRow>
                </>
              </TableBody>
            </Table>
          </TableContainer>
        </StyledDiv>
        {/* Here this component holds the price bar graph and also the table for showing the prices overview. */}
        <DiscountOverview
          data={data[energyRole]?.chartData || []}
          energyRole={energyRole}
          setEnergyRole={setEnergyRole}
          error={overviewError}
          onCloseFeedback={() => setOverviewError(false)}
        />
      </OuterSurface>
    </>
  )
}

export default Abschlag
